
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import './Organization.css';
import {
    IoLocationOutline,
    IoMailOutline,
    IoCallOutline,
    IoLinkOutline,
    IoDocumentTextOutline,
    IoPencil,
    IoSaveOutline,
    IoCloseOutline,
    IoBusinessOutline,
    IoTextOutline,
    IoShieldCheckmarkOutline,
    IoTimeOutline,
    IoSyncOutline,
    IoAlarmOutline
} from 'react-icons/io5';
import { getLoggedInUser } from '../../../../../utils/authHelper';
import { RefreshContext } from '../../../../../App';

// --- Configuration ---
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';

// --- API Helpers (No changes here) ---
const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);

const getCompanyDetails = async () => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication token not found.');
    const response = await fetch(`${API_BASE_URL}/api/get-company`, { headers: { 'Authorization': `Bearer ${token}` } });
    if (!response.ok) throw new Error(`Failed to fetch company details: ${response.status}`);
    const data = await response.json();
    const companyList = data.message || data.messgae;
    if (Array.isArray(companyList) && companyList.length > 0) {
        const companyInfo = companyList[0];
        if (companyInfo.companyLogo) {
            companyInfo.logoUrl = `data:image/png;base64,${companyInfo.companyLogo}`;
        }
    }
    return data;
};

const editCompanyDetails = async (companyId, detailsToUpdate) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication token not found.');
    const response = await fetch(`${API_BASE_URL}/api/edit-company?id=${companyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(detailsToUpdate)
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update company details.' }));
        throw new Error(errorData.message || 'An unknown error occurred during update.');
    }
    return response.json();
};

const editCompanyLogo = async (companyId, logoFile) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication token not found.');
    const formData = new FormData();
    formData.append('companyLogo', logoFile);
    const response = await fetch(`${API_BASE_URL}/api/edit-company-logo?companyId=${companyId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update company logo.' }));
        throw new Error(errorData.message || 'An unknown error occurred during logo update.');
    }
    return response.json();
};

// --- Skeleton Loader (No changes here) ---
const OrganizationSkeleton = () => (
    <div className="org-card skeleton-wrapper">
        <header className="org-header">
            <div className="org-logo skeleton-logo shimmer"></div>
            <div>
                <div className="skeleton-line shimmer" style={{ width: '300px', height: '28px', marginBottom: '12px' }}></div>
                <div className="skeleton-line shimmer" style={{ width: '80px', height: '24px' }}></div>
            </div>
        </header>
        <div className="org-details-grid">
            <div className="org-info-group">
                <div className="skeleton-line shimmer" style={{ width: '180px', height: '24px', marginBottom: '1.5rem' }}></div>
                {Array(3).fill(0).map((_, i) => (
                    <div key={i} className="skeleton-detail-item">
                        <div className="skeleton-line shimmer" style={{ width: '100px', height: '16px' }}></div>
                        <div className="skeleton-line shimmer" style={{ width: '180px', height: '16px' }}></div>
                    </div>
                ))}
            </div>
            <div className="org-info-group">
                <div className="skeleton-line shimmer" style={{ width: '180px', height: '24px', marginBottom: '1.5rem' }}></div>
                {Array(7).fill(0).map((_, i) => (
                     <div key={i} className="skeleton-detail-item">
                        <div className="skeleton-line shimmer" style={{ width: '100px', height: '16px' }}></div>
                        <div className="skeleton-line shimmer" style={{ width: '150px', height: '16px' }}></div>
                    </div>
                ))}
            </div>
            <div className="org-info-group">
                <div className="skeleton-line shimmer" style={{ width: '180px', height: '24px', marginBottom: '1.5rem' }}></div>
                {Array(9).fill(0).map((_, i) => (
                     <div key={i} className="skeleton-detail-item">
                        <div className="skeleton-line shimmer" style={{ width: '100px', height: '16px' }}></div>
                        <div className="skeleton-line shimmer" style={{ width: '160px', height: '16px' }}></div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);


// --- Main Organization Component ---
const Organization = () => {
    // --- State and Context (No changes) ---
    const [companyDetails, setCompanyDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedDetails, setEditedDetails] = useState(null);
    const [userRole, setUserRole] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const fileInputRef = useRef(null);
    const { triggerRefresh } = useContext(RefreshContext);

    // --- Data Fetching (No changes) ---
    const fetchCompanyDetails = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getCompanyDetails();
            const detailsList = data.messgae || data.message; 
            const details = detailsList && detailsList.length > 0 ? detailsList[0] : null;
            
            setCompanyDetails(details);
            setEditedDetails(details);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const user = getLoggedInUser();
        if (user && user.role) {
            setUserRole(user.role.name);
        }
        fetchCompanyDetails();
    }, [fetchCompanyDetails]);
    
    useEffect(() => {
        return () => {
            if (logoPreview) URL.revokeObjectURL(logoPreview);
        };
    }, [logoPreview]);

    // --- Event Handlers (No changes) ---
    const handleEditToggle = () => {
        setEditedDetails(companyDetails);
        setIsEditMode(!isEditMode);
        setError(null);
        setSuccessMessage(null);
        setLogoFile(null);
        if (logoPreview) {
            URL.revokeObjectURL(logoPreview);
            setLogoPreview(null);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditedDetails(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            if (logoPreview) URL.revokeObjectURL(logoPreview);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    // --- Save Handler (No changes) ---
    const handleSaveChanges = async () => {
        if (!editedDetails || !editedDetails.companyId) {
            setError("Company ID is missing. Cannot save changes.");
            return;
        }
        setIsSaving(true);
setError(null);
        setSuccessMessage(null);
        try {
            const { companyId, createdAt, updatedAt, companyLogo, logoUrl, ...payload } = editedDetails;

            if (logoFile) {
                await editCompanyLogo(companyId, logoFile);
            }
            
            if (payload.alertTime && payload.alertTime.length === 5) {
                payload.alertTime = `${payload.alertTime}:00`;
            }
            if (payload.backupTimeM && payload.backupTimeM.length === 5) {
                payload.backupTimeM = `${payload.backupTimeM}:00`;
            }
            if (payload.backupTimeE && payload.backupTimeE.length === 5) {
                payload.backupTimeE = `${payload.backupTimeE}:00`;
            }

            await editCompanyDetails(companyId, payload);
            
            setSuccessMessage('Company details updated successfully!');
            
            triggerRefresh();

            await fetchCompanyDetails();
            setIsEditMode(false);

            setLogoFile(null);
            if (logoPreview) {
                URL.revokeObjectURL(logoPreview);
                setLogoPreview(null);
            }

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };
    
    // --- MODIFICATION: Updated formatDate function to include '/' ---
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        const date = new Date(dateString);
        
        const pad = (num) => num.toString().padStart(2, '0');

        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());

        // Returns date in dd/mm/yyyy hh:mm format
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    // --- (No changes below this line) ---
    const formatTime = (timeString) => {
        if (!timeString) return 'Not Set';
        return timeString.substring(0, 5);
    };

    if (isLoading) {
        return <div className="organization-container"><OrganizationSkeleton /></div>;
    }

    const canEdit = userRole === 'SUPER_ADMIN';
    const currentData = isEditMode ? editedDetails : companyDetails;
    const displayLogo = logoPreview || currentData?.logoUrl;

    return (
        <div className="organization-container">
            {successMessage && <div className="org-success-message">{successMessage}</div>}
            {error && <div className="org-error-message">{error}</div>}
            
            {!companyDetails && !error && (
                <div className="org-empty-message">No company details available.</div>
            )}

            {companyDetails && (
                <div className="org-card">
                    <header className="org-header">
                        <div className="org-logo-container">
                            {displayLogo && (
                                <img src={displayLogo} alt={`${currentData.companyName} Logo`} className="org-logo" />
                            )}
                            {isEditMode && (
                                <button className="org-logo-edit-btn" onClick={() => fileInputRef.current.click()} disabled={isSaving}>
                                    <IoPencil />
                                </button>
                            )}
                             <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />
                        </div>
                        <div>
                            {isEditMode ? (
                                <input type="text" name="companyName" value={currentData.companyName || ''} onChange={handleInputChange} className="org-input org-input-h1" placeholder="Company Name" />
                            ) : (
                                <h1 className="org-company-name">{currentData.companyName}</h1>
                            )}
                            {currentData.active && <span className="org-status-badge active">Active</span>}
                        </div>
                        {canEdit && !isEditMode && (
                            <button className="org-edit-btn" onClick={handleEditToggle}>
                                <IoPencil /> Edit Details
                            </button>
                        )}
                    </header>
                    
                    <div className="org-details-grid">
                        {/* General Information Group */}
                        <div className="org-info-group">
                            <h3>General Information</h3>
                            <dl>
                                <div className="org-detail-item">
                                    <dt><IoBusinessOutline className="org-icon" />Short Name</dt>
                                    <dd>{isEditMode ? <input type="text" name="companyShortName" value={currentData.companyShortName || ''} onChange={handleInputChange} className="org-input" /> : (currentData.companyShortName || 'N/A')}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoTextOutline className="org-icon" />Abbreviation</dt>
                                    <dd>{isEditMode ? <input type="text" name="company_abbr" value={currentData.company_abbr || ''} onChange={handleInputChange} className="org-input" /> : (currentData.company_abbr || 'N/A')}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoLinkOutline className="org-icon" />Website</dt>
                                    <dd>{isEditMode ? <input type="text" name="websiteUrl" value={currentData.websiteUrl || ''} onChange={handleInputChange} className="org-input" /> : <a href={currentData.websiteUrl} target="_blank" rel="noopener noreferrer" className="org-contact-link">{currentData.websiteUrl || 'N/A'}</a>}</dd>
                                </div>
                            </dl>
                        </div>

                        {/* Contact & Registration Group */}
                        <div className="org-info-group">
                            <h3>Contact & Registration</h3>
                            <dl>
                                 <div className="org-detail-item">
                                    <dt><IoMailOutline className="org-icon" />Email</dt>
                                    <dd>{isEditMode ? <input type="email" name="emailAddress" value={currentData.emailAddress || ''} onChange={handleInputChange} className="org-input" /> : <a href={`mailto:${currentData.emailAddress}`} className="org-contact-link">{currentData.emailAddress || 'N/A'}</a>}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoCallOutline className="org-icon" />Telephone</dt>
                                    <dd>{isEditMode ? <input type="tel" name="telephoneNumber" value={currentData.telephoneNumber || ''} onChange={handleInputChange} className="org-input" /> : <a href={`tel:${currentData.telephoneNumber}`} className="org-contact-link">{currentData.telephoneNumber || 'N/A'}</a>}</dd>
                                </div>
                                 <div className="org-detail-item">
                                    <dt><IoLocationOutline className="org-icon" />Address</dt>
                                    <dd>{isEditMode ? <textarea name="address" value={currentData.address || ''} onChange={handleInputChange} className="org-input" rows="2" /> : (currentData.address || 'N/A')}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoDocumentTextOutline className="org-icon" />PAN</dt>
                                    <dd>{isEditMode ? <input type="text" name="panNumber" value={currentData.panNumber || ''} onChange={handleInputChange} className="org-input" /> : (currentData.panNumber || 'N/A')}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoDocumentTextOutline className="org-icon" />GST</dt>
                                    <dd>{isEditMode ? <input type="text" name="gstNumber" value={currentData.gstNumber || ''} onChange={handleInputChange} className="org-input" /> : (currentData.gstNumber || 'N/A')}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoDocumentTextOutline className="org-icon" />TIN</dt>
                                    <dd>{isEditMode ? <input type="text" name="tinNumber" value={currentData.tinNumber || ''} onChange={handleInputChange} className="org-input" /> : (currentData.tinNumber || 'N/A')}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoDocumentTextOutline className="org-icon" />TAN</dt>
                                    <dd>{isEditMode ? <input type="text" name="tanNumber" value={currentData.tanNumber || ''} onChange={handleInputChange} className="org-input" /> : (currentData.tanNumber || 'N/A')}</dd>
                                </div>
                            </dl>
                        </div>

                        {/* System & Configuration Group */}
                        <div className="org-info-group">
                            <h3>System & Configuration</h3>
                             <dl>
                                <div className="org-detail-item checkbox-item">
                                    <dt><IoShieldCheckmarkOutline className="org-icon" />Active Status</dt>
                                    <dd>{isEditMode ? <label className="org-switch"><input type="checkbox" name="active" checked={!!currentData.active} onChange={handleInputChange} /><span className="slider"></span></label> : (currentData.active ? 'Yes' : 'No')}</dd>
                                </div>
                                <div className="org-detail-item checkbox-item">
                                    <dt><IoShieldCheckmarkOutline className="org-icon" />Enable 2FA</dt>
                                    <dd>{isEditMode ? <label className="org-switch"><input type="checkbox" name="enable_2fa" checked={!!currentData.enable_2fa} onChange={handleInputChange} /><span className="slider"></span></label> : (currentData.enable_2fa ? 'Yes' : 'No')}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoAlarmOutline className="org-icon" />Daily Alert Time</dt>
                                    <dd>
                                        {isEditMode ? 
                                            <input type="time" name="alertTime" value={formatTime(currentData.alertTime)} onChange={handleInputChange} className="org-input" /> 
                                            : formatTime(currentData.alertTime)
                                        }
                                    </dd>
                                </div>
                                 <div className="org-detail-item">
                                    <dt><IoTimeOutline className="org-icon" />Morning Backup Time</dt>
                                    <dd>
                                        {isEditMode ? 
                                            <input type="time" name="backupTimeM" value={formatTime(currentData.backupTimeM)} onChange={handleInputChange} className="org-input" /> 
                                            : formatTime(currentData.backupTimeM)
                                        }
                                    </dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoTimeOutline className="org-icon" />Evening Backup Time</dt>
                                    <dd>
                                        {isEditMode ? 
                                            <input type="time" name="backupTimeE" value={formatTime(currentData.backupTimeE)} onChange={handleInputChange} className="org-input" /> 
                                            : formatTime(currentData.backupTimeE)
                                        }
                                    </dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoMailOutline className="org-icon" />Alert Mail ID</dt>
                                    <dd>
                                        {isEditMode ?
                                            <input type="email" name="alertMailId" value={currentData.alertMailId || ''} onChange={handleInputChange} className="org-input" />
                                            : <a href={`mailto:${currentData.alertMailId}`} className="org-contact-link">{currentData.alertMailId || 'N/A'}</a>
                                        }
                                    </dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoTimeOutline className="org-icon" />Token Validity (Hours)</dt>
                                    <dd>
                                        {isEditMode ?
                                            <input type="number" name="tokenValidityHours" value={currentData.tokenValidityHours || ''} onChange={handleInputChange} className="org-input" />
                                            : (currentData.tokenValidityHours !== null ? currentData.tokenValidityHours : 'Not Set')
                                        }
                                    </dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoTimeOutline className="org-icon" />Created At</dt>
                                    <dd>{formatDate(currentData.createdAt)}</dd>
                                </div>
                                <div className="org-detail-item">
                                    <dt><IoSyncOutline className="org-icon" />Last Updated</dt>
                                    <dd>{formatDate(currentData.updatedAt)}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    {isEditMode && (
                        <footer className="org-edit-footer">
                            <button className="org-action-btn cancel-btn" onClick={handleEditToggle} disabled={isSaving}>
                                <IoCloseOutline /> Cancel
                            </button>
                            <button className="org-action-btn save-btn" onClick={handleSaveChanges} disabled={isSaving}>
                                <IoSaveOutline /> {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </footer>
                    )}
                </div>
            )}
        </div>
    );
};

export default Organization;